import React, { Component } from 'react';
import cx from 'classnames';
import Fuse from 'fuse.js';
import moment from 'moment';
import download from 'downloadjs';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, Card, CardBody } from 'reactstrap';
import Icon from 'shared/components/Icon';
import { Title, TitleIcon } from 'shared/components/SectionTitle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { Currency } from 'shared/components/Transactions/components';
import { BillingCustomerDetailStatus, ProjectBillingStatusDetail } from '../../constants';
import { fetchInvoice } from 'shared/store/modules/subscribers/effects';
import ProjectRowResult from './ProjectRowResult';
import ProjectReference from './ProjectReference';
import styles from './ResultsTable.styles';
import sctheme from 'app/styles/sctheme';
import InvoicingTotals from './InvoicingTotals';
import ProjectCount from './ProjectCount';
import { CannotDeleteTooltip } from './CannotDeleteTooltip';

const StatusIcon = ({ status, canFinalize, isInvoiceGenerated }) => {
  if (status === false || (!status && !canFinalize && isInvoiceGenerated === false)) {
    return <Icon icon="highlight_off" style={{ color: 'red' }} />;
  }

  if (status === true) {
    return <Icon icon="check" style={{ color: 'green' }} />;
  }

  return null;
};

class ResultsTable extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isSearching: false,
      query: '',
      matches: [],
      showProjectInfo: null,
      totalAllocationAmount: null,
      totalUtilityCredit: null,
      totalAllocationCost: null,
      showTooltip: false
    };

    this.focusSearchInput = this.focusSearchInput.bind(this);
  }

  componentDidMount() {
    if (this.props.items) {
      this.aggregateBillingDetails(this.props.items);
    } else {
      this.setState({ loading: false });
    }
  }

  async handleInvoiceDownload(item, month, year) {
    const { displayName, invoiceNumber, invoiceId } = item;
    const monthName = moment.months(month - 1);
    const { payload } = await this.props.fetchInvoice(invoiceId);
    const filename = `${monthName} ${year} Statement #${invoiceNumber} - ${displayName}.pdf`;

    download(payload, filename, 'application/pdf');
  }

  aggregateBillingDetails(subscriberData) {
    this.setState({ loading: true });
    let totalAllocationAmount = 0;
    let totalUtilityCredit = 0;
    let totalAllocationCost = 0;

    subscriberData?.forEach(subscriber => {
      if (subscriber.projectDetails && Array.isArray(subscriber.projectDetails)) {
        subscriber.projectDetails.forEach(project => {
          totalAllocationAmount += project.allocationAmount || 0;
          totalUtilityCredit += project.utilityCredit || 0;
          totalAllocationCost += project.allocationCost || 0;
        });
      }
    });

    this.setState({
      totalAllocationAmount,
      totalUtilityCredit,
      totalAllocationCost,
      loading: false
    });
  }

  renderDownloadLink(item, month, year) {
    return (
      <a
        href="/"
        onClick={e => {
          e.preventDefault();
          this.handleInvoiceDownload(item, month, year);
        }}
      >
        <Icon icon="get_app" />
      </a>
    );
  }

  componentDidUpdate(prevProps, prevState) {
    const { isSearching } = this.state;

    if (isSearching !== prevState.isSearching) {
      if (!isSearching) {
        this.setState({ query: '' });
      } else {
        this.focusSearchInput();
      }
    }
    if (prevProps.items !== this.props.items) {
      this.aggregateBillingDetails(this.props.items);
    }
  }

  focusSearchInput() {
    this.searchInput.focus();
  }

  handleSearchChange = e => {
    const { searchKeys } = this.props;

    const fuse = new Fuse(this.props.items, {
      distance: 100,
      keys: searchKeys,
      location: 0,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      shouldSort: true,
      threshold: 0.2
    });

    const matches = fuse.search(e.target.value);

    this.setState({
      [e.target.name]: e.target.value,
      matches
    });
  };

  toggleSearch = status => {
    this.setState(({ isSearching }) => ({
      isSearching: status || !isSearching
    }));
  };

  handleShowTooltip = bool => {
    if (this?.props?.items?.length === 1) {
      this.setState({ showTooltip: bool });
    }
  };

  renderSearchBar() {
    const { isSearching } = this.state;
    const { items, searchKeys } = this.props;

    if (!!items.length && !!searchKeys.length) {
      return (
        <div className="title-search">
          <input
            className={cx('form-control title-search-input', {
              'title-search-input--expanded': isSearching
            })}
            name="query"
            onChange={this.handleSearchChange}
            placeholder="Search&hellip;"
            ref={input => (this.searchInput = input)}
            value={this.state.query}
          />
          <Button className="ml-1" color="light" onClick={this.toggleSearch.bind(null, false)} size="sm">
            {isSearching ? <Icon icon="cancel" /> : <Icon icon="search" />}
          </Button>
        </div>
      );
    }
    return null;
  }

  render() {
    const {
      actions,
      title,
      icon,
      emptyText,
      month,
      year,
      handleDeleteSubscriber,
      handleDeleteProject,
      handleSubscriberDetailsToggle,
      showSubscriberInfo,
      canFinalize,
      canGenerate,
      successfullyGeneratedInvoiceCount,
      totalInvoiceCount,
      invoicedBeforeLaunchDate
    } = this.props;

    const handleProjectDetailsToggle = id => {
      this.setState(prevState => {
        if (prevState.showProjectInfo === id) {
          return { showProjectInfo: null };
        }
        return { showProjectInfo: id };
      });
    };

    let results = this.props.items.slice(0);

    if (this.state.query && this.state.query !== '') {
      if (this.state.matches.length) {
        results = this.state.matches;
      } else {
        results = [];
      }
    }

    return (
      <div css={styles}>
        <Title className="d-flex align-items-center pr-0 mb-0">
          <TitleIcon icon={icon} />
          {title}
          {this.renderSearchBar()}
          {actions && actions}
        </Title>

        {!!results.length ? (
          <div className="d-flex" style={{ flexDirection: 'column' }}>
            <Card className="mb-3 p-3">
              <InvoicingTotals
                results={results}
                canFinalize={canFinalize}
                canGenerate={canGenerate}
                totalAllocationCost={this.state.totalAllocationCost}
                totalUtilityCredit={this.state.totalUtilityCredit}
                totalAllocationAmount={this.state.totalAllocationAmount}
                successfullyGeneratedInvoiceCount={successfullyGeneratedInvoiceCount}
                totalInvoiceCount={totalInvoiceCount}
                invoicedBeforeLaunchDate={invoicedBeforeLaunchDate}
              />
            </Card>
            <Card>
              <CardBody>
                <div>
                  <div className="table-header font-weight-bold">
                    <div style={{ paddingLeft: '2.35rem' }}>Customer Name</div>
                    <div className="text-right">Projects</div>
                    <div className="text-right">Total Invoice</div>
                    <div className="text-right">Created</div>
                    <div className="text-right" style={{ marginRight: '-1rem' }}>
                      Sent
                    </div>
                    <div />
                  </div>
                  {results.map(item => {
                    const toggleSubscriber = showSubscriberInfo?.find(
                      toggle => toggle.id === item.billingCustomerDetailId
                    );
                    const projectsReferenceActive = item.projectDetails.filter(
                      project => project.status === ProjectBillingStatusDetail.InAnotherBillingForGivenPeriod
                    ).length;
                    const projectsReferenceRemoved = item.projectDetails.filter(
                      project => project.status === ProjectBillingStatusDetail.OtherBillingReferenceRemoved
                    ).length;

                    if ((item?.projectDetails?.length === 0 && item.totalProjects > 0) || item?.isDeleted) {
                      return null;
                    }
                    return (
                      <div key={item.customerId}>
                        <div className="results-wrapper">
                          <div>
                            <div className="table-results">
                              <div className="d-flex align-items-center">
                                {item.projectDetails.length > 0 ? (
                                  <FontAwesomeIcon
                                    icon={toggleSubscriber?.open ? faMinus : faPlus}
                                    className="subscriber-toggle mr-2"
                                    onClick={() => handleSubscriberDetailsToggle(item.billingCustomerDetailId)}
                                  />
                                ) : (
                                  <div className="mr-3 pr-2" />
                                )}
                                <div>
                                  <Link to={`/admin/subscribers/${item.customerId}`}>{item.displayName}</Link>
                                </div>
                              </div>
                              <div>
                                <div
                                  className="text-right"
                                  style={{
                                    color: item.projectDetails.length > 0 ? sctheme.blues.secondary : sctheme.black,
                                    cursor: item.projectDetails.length > 0 ? 'pointer' : 'auto',
                                    pointerEvents: item.projectDetails.length > 0 ? 'auto' : 'none'
                                  }}
                                  onClick={() => handleProjectDetailsToggle(item.billingCustomerDetailId)}
                                >
                                  <div>
                                    <div className="position-relative">
                                      {item.projectDetails.length === 0 ? '—' : item.projectDetails.length}
                                      <>
                                        {projectsReferenceActive >= 1 || projectsReferenceRemoved >= 1 ? (
                                          <ProjectCount
                                            projectsReferenceActive={projectsReferenceActive}
                                            projectsReferenceRemoved={projectsReferenceRemoved}
                                          />
                                        ) : null}
                                        {this.state.showProjectInfo === item.billingCustomerDetailId ? (
                                          <ProjectReference month={month} year={year} item={item} />
                                        ) : null}
                                      </>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="text-right">
                                {invoicedBeforeLaunchDate || item?.isInvoiceGenerated === false ? (
                                  '--'
                                ) : [
                                    BillingCustomerDetailStatus.InvoiceGenerated,
                                    BillingCustomerDetailStatus.FinalizedCompletely,
                                    BillingCustomerDetailStatus.FinalizedPartially,
                                    BillingCustomerDetailStatus.InvoiceRegenerated
                                  ].includes(item?.status) ? (
                                  <Currency amount={item?.invoiceTotalCost} />
                                ) : null}
                              </div>
                              <div className="text-right">
                                {item.isInvoiceGenerated && this.renderDownloadLink(item, month, year)}
                                {item.isInvoiceGenerated === false && (
                                  <Icon icon="highlight_off" style={{ color: 'red' }} />
                                )}
                              </div>
                              <div className="text-right" style={{ marginRight: '-1.5rem' }}>
                                <StatusIcon
                                  status={item.isNotified}
                                  canFinalize={canFinalize}
                                  canGenerate={canGenerate}
                                  isInvoiceGenerated={item.isInvoiceGenerated}
                                />
                              </div>
                              <div
                                className="text-right position-relative"
                                onMouseEnter={() => this.handleShowTooltip(true)}
                                onMouseLeave={() => this.handleShowTooltip(false)}
                              >
                                {!canGenerate && !canFinalize ? null : (
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className={`mr-2 trash ${results.length === 1 ? 'disabled' : ''}`}
                                    onClick={() =>
                                      handleDeleteSubscriber(item.billingCustomerDetailId, item.displayName)
                                    }
                                  />
                                )}
                                {this.state.showTooltip && <CannotDeleteTooltip />}
                              </div>
                            </div>
                          </div>
                        </div>
                        {toggleSubscriber?.open ? (
                          <div className="project-table-wrapper ">
                            <div className="project-table-header">
                              <div>Project(s)</div>
                              <div className="text-right">In Existing Batch</div>
                              <div className="text-right">Project Invoice</div>
                              <div className="text-right">Utility Credit</div>
                              <div className="text-right">Allocation (kWh)</div>
                              <div />
                            </div>
                            {item.projectDetails.map(project => (
                              <ProjectRowResult
                                month={month}
                                year={year}
                                project={project}
                                item={item}
                                handleDeleteProject={handleDeleteProject}
                                handleDeleteSubscriber={handleDeleteSubscriber}
                                totalInvoiceCount={totalInvoiceCount}
                              />
                            ))}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </CardBody>
            </Card>
          </div>
        ) : !!results && this.state.query ? (
          <div className="alert alert-info text-center">
            <em>No results found for "{this.state.query}</em>
          </div>
        ) : (
          <div className="alert alert-info text-center">
            <em>{emptyText}</em>
          </div>
        )}
      </div>
    );
  }
}

const mapDispatchToProps = {
  fetchInvoice
};

export default connect(null, mapDispatchToProps)(ResultsTable);
